import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./styles/Executive.css";

const EXECUTIVE_API = "https://salathai.co.tz/mrms/Executive.php";

export default function ExecutiveOrders() {
    const [orders, setOrders] = useState([]);
    const [filteredOrders, setFilteredOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(5);
    
    const [companyBalances, setCompanyBalances] = useState({
        PIKI: 0,
        POLYGOLD: 0,
        THIRD_MAN: 0
    });
    const [formData, setFormData] = useState({
        order_number: "",
        company_name: "",
        order_date: new Date().toISOString().split('T')[0],
        total_amount: "",
        status: "PENDING",
        payment_method: "",
        payment_date: ""
    });

    const companyOptions = ["PIKI", "POLYGOLD", "THIRD MAN"];
    const statusOptions = ["PENDING", "PAID"];
    const paymentMethodOptions = ["CASH", "M-PESA", "BANK TRANSFER", "CHEQUE", "CREDIT"];

    const getToken = () => localStorage.getItem("token");

    useEffect(() => {
        const token = getToken();
        if (!token) {
            Swal.fire("Session Expired", "Please login again", "warning");
            return;
        }
        loadOrders();
    }, []);

    useEffect(() => {
        filterOrders();
        calculateBalances();
    }, [searchTerm, orders]);

    // Reset to page 1 when search changes
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm]);

    const filterOrders = () => {
        if (!searchTerm.trim()) {
            setFilteredOrders(orders);
            return;
        }
        const term = searchTerm.toLowerCase().trim();
        const filtered = orders.filter(order =>
            order.order_number.toLowerCase().includes(term) ||
            order.company_name.toLowerCase().includes(term) ||
            order.status.toLowerCase().includes(term)
        );
        setFilteredOrders(filtered);
    };

    const calculateBalances = () => {
        const balances = {
            PIKI: 0,
            POLYGOLD: 0,
            THIRD_MAN: 0
        };

        orders.forEach(order => {
            const company = order.company_name;
            if (balances.hasOwnProperty(company)) {
                if (order.status === "PENDING") {
                    balances[company] += parseFloat(order.total_amount || 0);
                }
            }
        });

        setCompanyBalances(balances);
    };

    // Get current page data
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredOrders.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

    // Change page
    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    // Next/Previous page
    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const loadOrders = async () => {
        const token = getToken();
        if (!token) return;

        setLoading(true);
        try {
            const url = `${EXECUTIVE_API}?token=${token}`;
            const res = await fetch(url);
            const text = await res.text();
            
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}: ${text}`);
            }
            
            const data = JSON.parse(text);
            if (Array.isArray(data)) {
                setOrders(data);
                setFilteredOrders(data);
            } else if (data.error) {
                Swal.fire("Error", data.error, "error");
            }
        } catch (err) {
            console.error("Load Orders Error:", err);
            Swal.fire("Error", "Cannot connect to server", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = getToken();
        
        if (!token) {
            Swal.fire("Error", "Authentication required", "error");
            return;
        }

        if (!formData.order_number.trim()) {
            Swal.fire("Error", "Order number is required", "error");
            return;
        }

        if (!formData.company_name) {
            Swal.fire("Error", "Company name is required", "error");
            return;
        }

        const totalAmount = parseFloat(formData.total_amount);
        if (!totalAmount || totalAmount <= 0) {
            Swal.fire("Error", "Total amount must be greater than 0", "error");
            return;
        }

        try {
            const submitData = {
                order_number: formData.order_number.trim(),
                company_name: formData.company_name,
                order_date: formData.order_date,
                total_amount: totalAmount,
                status: formData.status,
                payment_method: formData.payment_method,
                payment_date: formData.payment_date || null
            };

            let url = `${EXECUTIVE_API}?token=${token}`;
            let method = "POST";

            if (editingId) {
                url = `${EXECUTIVE_API}?token=${token}`;
                method = "PUT";
                submitData.id = editingId;
            }

            const res = await fetch(url, {
                method: method,
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(submitData),
            });

            const data = await res.json();

            if (data.error) {
                if (res.status === 409) {
                    Swal.fire("Duplicate", "Order number already exists", "warning");
                } else {
                    Swal.fire("Error", data.error, "error");
                }
            } else {
                Swal.fire("Success", data.message || "Order saved successfully", "success");
                setShowForm(false);
                setEditingId(null);
                resetForm();
                loadOrders();
            }
        } catch (err) {
            console.error("Submit Error:", err);
            Swal.fire("Error", "Failed to save order", "error");
        }
    };

    const handleEdit = (order) => {
        setEditingId(order.id);
        setFormData({
            order_number: order.order_number,
            company_name: order.company_name,
            order_date: order.order_date || new Date().toISOString().split('T')[0],
            total_amount: order.total_amount.toString(),
            status: order.status || "PENDING",
            payment_method: order.payment_method || "",
            payment_date: order.payment_date || ""
        });
        setShowForm(true);
    };

    const handleDelete = async (id, orderNumber) => {
        const result = await Swal.fire({
            title: "Delete Order?",
            html: `Are you sure you want to delete order <strong>${orderNumber}</strong>?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!",
            cancelButtonText: "Cancel"
        });

        if (!result.isConfirmed) return;

        const token = getToken();
        if (!token) {
            Swal.fire("Error", "Authentication required", "error");
            return;
        }

        try {
            const res = await fetch(`${EXECUTIVE_API}?token=${token}&id=${id}`, {
                method: "DELETE"
            });

            const data = await res.json();

            if (data.error) {
                Swal.fire("Error", data.error, "error");
            } else {
                Swal.fire("Deleted!", "Order has been deleted", "success");
                loadOrders();
            }
        } catch (err) {
            console.error("Delete Error:", err);
            Swal.fire("Error", "Failed to delete order", "error");
        }
    };

    const handleStatusChange = async (id, currentStatus) => {
        const newStatus = currentStatus === "PENDING" ? "PAID" : "PENDING";
        
        const result = await Swal.fire({
            title: `Change Status to ${newStatus}?`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: newStatus === "PAID" ? "#10b981" : "#f59e0b",
            cancelButtonColor: "#3085d6",
            confirmButtonText: `Yes, mark as ${newStatus}`,
            cancelButtonText: "Cancel"
        });

        if (!result.isConfirmed) return;

        const token = getToken();
        if (!token) {
            Swal.fire("Error", "Authentication required", "error");
            return;
        }

        try {
            const res = await fetch(`${EXECUTIVE_API}?token=${token}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    id: id,
                    status: newStatus
                })
            });

            const data = await res.json();

            if (data.error) {
                Swal.fire("Error", data.error, "error");
            } else {
                Swal.fire("Updated!", `Status changed to ${newStatus}`, "success");
                loadOrders();
            }
        } catch (err) {
            console.error("Status Update Error:", err);
            Swal.fire("Error", "Failed to update status", "error");
        }
    };

    const resetForm = () => {
        setFormData({
            order_number: "",
            company_name: "",
            order_date: new Date().toISOString().split('T')[0],
            total_amount: "",
            status: "PENDING",
            payment_method: "",
            payment_date: ""
        });
        setEditingId(null);
    };

    const formatCurrency = (amount) => 
        `TZS ${Number(amount || 0).toLocaleString("en-US", { minimumFractionDigits: 0 })}`;

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const totalOrders = orders.length;
    const totalPending = orders.filter(o => o.status === "PENDING").length;
    const totalPaid = orders.filter(o => o.status === "PAID").length;
    const totalAmount = orders.reduce((sum, o) => sum + (o.total_amount || 0), 0);

    if (loading) {
        return (
            <div className="executive-loader">
                <div className="executive-spinner"></div>
                <p>Loading orders...</p>
            </div>
        );
    }

    return (
        <div className="executive-container">
            {/* Header */}
            <div className="executive-header">
                <div>
                    <h1>Executive Orders</h1>
                    <p>{totalOrders} total orders</p>
                </div>
                <button className="executive-btn executive-btn-primary" onClick={() => { resetForm(); setShowForm(true); }}>
                    New Order
                </button>
            </div>

            {/* Company Balances */}
            <div className="executive-balances">
                <div className="balance-card">
                    <span className="balance-label">PIKI</span>
                    <span className="balance-amount">{formatCurrency(companyBalances.PIKI)}</span>
                </div>
                <div className="balance-card">
                    <span className="balance-label">POLYGOLD</span>
                    <span className="balance-amount">{formatCurrency(companyBalances.POLYGOLD)}</span>
                </div>
                <div className="balance-card">
                    <span className="balance-label">THIRD MAN</span>
                    <span className="balance-amount">{formatCurrency(companyBalances.THIRD_MAN)}</span>
                </div>
                <div className="balance-card total">
                    <span className="balance-label">Total Debt</span>
                    <span className="balance-amount">{formatCurrency(companyBalances.PIKI + companyBalances.POLYGOLD + companyBalances.THIRD_MAN)}</span>
                </div>
            </div>

            {/* Stats */}
            <div className="executive-stats">
                <div className="stat">
                    <span className="stat-value">{totalOrders}</span>
                    <span className="stat-label">Total Orders</span>
                </div>
                <div className="stat">
                    <span className="stat-value">{totalPending}</span>
                    <span className="stat-label">Pending</span>
                </div>
                <div className="stat">
                    <span className="stat-value">{totalPaid}</span>
                    <span className="stat-label">Paid</span>
                </div>
                <div className="stat">
                    <span className="stat-value">{formatCurrency(totalAmount)}</span>
                    <span className="stat-label">Total Value</span>
                </div>
            </div>

            {/* Search */}
            <div className="executive-search">
                <input
                    type="text"
                    placeholder="Search orders..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="executive-search-input"
                />
            </div>

            {/* Table */}
            <div className="executive-table-wrapper">
                <table className="executive-table">
                    <thead>
                        <tr>
                            <th>Order #</th>
                            <th>Company</th>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Status</th>
                            <th>Payment</th>
                            <th>Payment Date</th>
                            <th>Issued By</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((order) => (
                                <tr key={order.id}>
                                    <td><strong>{order.order_number}</strong></td>
                                    <td>{order.company_name}</td>
                                    <td>{formatDate(order.order_date)}</td>
                                    <td>{formatCurrency(order.total_amount)}</td>
                                    <td>
                                        <button
                                            className={`executive-status ${order.status.toLowerCase()}`}
                                            onClick={() => handleStatusChange(order.id, order.status)}
                                        >
                                            {order.status}
                                        </button>
                                    </td>
                                    <td>{order.payment_method || "-"}</td>
                                    <td>{formatDate(order.payment_date)}</td>
                                    <td>{order.issued_by || "-"}</td>
                                    <td>
                                        <div className="executive-actions">
                                            <button className="executive-action edit" onClick={() => handleEdit(order)}>Edit</button>
                                            <button className="executive-action delete" onClick={() => handleDelete(order.id, order.order_number)}>Delete</button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="9" className="executive-empty">
                                    <p>{searchTerm ? "No matching orders" : "No orders yet"}</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            {filteredOrders.length > itemsPerPage && (
                <div className="executive-pagination">
                    <button 
                        className="pagination-btn"
                        onClick={prevPage}
                        disabled={currentPage === 1}
                    >
                        Previous
                    </button>
                    
                    <div className="pagination-numbers">
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                            <button
                                key={number}
                                className={`pagination-number ${currentPage === number ? 'active' : ''}`}
                                onClick={() => paginate(number)}
                            >
                                {number}
                            </button>
                        ))}
                    </div>
                    
                    <button 
                        className="pagination-btn"
                        onClick={nextPage}
                        disabled={currentPage === totalPages}
                    >
                        Next
                    </button>
                </div>
            )}

            {/* Showing info */}
            {filteredOrders.length > 0 && (
                <div className="executive-pagination-info">
                    Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredOrders.length)} of {filteredOrders.length} entries
                </div>
            )}

            {/* Form Modal */}
            {showForm && (
                <div className="executive-modal" onClick={() => setShowForm(false)}>
                    <div className="executive-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="executive-modal-header">
                            <h2>{editingId ? "Edit Order" : "New Order"}</h2>
                            <button className="executive-modal-close" onClick={() => setShowForm(false)}>×</button>
                        </div>
                        
                        <form onSubmit={handleSubmit}>
                            <div className="executive-form">
                                <div className="executive-form-group">
                                    <label>Order Number</label>
                                    <input
                                        type="text"
                                        placeholder="e.g., 2026-08-03-001"
                                        value={formData.order_number}
                                        onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                                        required
                                    />
                                </div>
                                
                                <div className="executive-form-group">
                                    <label>Company</label>
                                    <select
                                        value={formData.company_name}
                                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                                        required
                                    >
                                        <option value="">Select Company</option>
                                        {companyOptions.map(company => (
                                            <option key={company} value={company}>{company}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="executive-form-group">
                                    <label>Order Date</label>
                                    <input
                                        type="date"
                                        value={formData.order_date}
                                        onChange={(e) => setFormData({ ...formData, order_date: e.target.value })}
                                    />
                                </div>

                                <div className="executive-form-group">
                                    <label>Total Amount</label>
                                    <input
                                        type="number"
                                        step="1000"
                                        placeholder="0.00"
                                        value={formData.total_amount}
                                        onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                                        required
                                    />
                                </div>

                                <div className="executive-form-group">
                                    <label>Status</label>
                                    <select
                                        value={formData.status}
                                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    >
                                        {statusOptions.map(status => (
                                            <option key={status} value={status}>{status}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="executive-form-group">
                                    <label>Payment Method</label>
                                    <select
                                        value={formData.payment_method}
                                        onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                                    >
                                        <option value="">Select Method</option>
                                        {paymentMethodOptions.map(method => (
                                            <option key={method} value={method}>{method}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="executive-form-group">
                                    <label>Payment Date</label>
                                    <input
                                        type="date"
                                        value={formData.payment_date}
                                        onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                                    />
                                </div>
                            </div>
                            
                            <div className="executive-form-actions">
                                <button type="button" className="executive-btn executive-btn-secondary" onClick={() => setShowForm(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="executive-btn executive-btn-primary">
                                    {editingId ? "Update" : "Create"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}